import React, { useEffect, useRef } from "react";
import "./style.scss";

// ** Where the dashboard now lives.
export const NEW_DASHBOARD_URL = "https://app.uzeli.com";

// ** Date this page starts redirecting to NEW_DASHBOARD_URL.
const REDIRECT_DATE_LABEL = "Sept 1, 2026";

/**
 * Announces the app.uzeli.com move at the very top of the page.
 *
 * Mounted once at the app root so it covers both the authenticated layouts
 * (vertical/horizontal, which render a `.wrapper` with fixed navbar + sidebar)
 * and BlankLayout pages like login, which have no wrapper at all.
 *
 * Not dismissible on purpose: this is a hard cutover notice, and a user who
 * dismisses it once would otherwise never see it again before the redirect.
 *
 * The banner publishes its rendered height as `--new-address-banner-height`,
 * because the surrounding chrome is fixed-position / viewport-height and has to
 * be offset by however tall the banner actually is (it wraps to two lines on
 * small screens). See style.scss for the consumers.
 *
 * This goes on <html>, NOT <body>: useSkin() does a blanket
 * `body.classList.remove(...body.classList)` on every skin change, which would
 * strip the marker class and silently break every offset below.
 */
const NewAddressBanner = () => {
  const bannerRef = useRef(null);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return undefined;

    const root = document.documentElement;
    root.classList.add("has-new-address-banner");

    const syncHeight = () => {
      root.style.setProperty(
        "--new-address-banner-height",
        `${banner.offsetHeight}px`
      );
    };

    syncHeight();

    // ResizeObserver is unavailable on some older embedded webviews; the
    // initial measurement above is still correct there.
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncHeight)
        : null;
    observer?.observe(banner);
    window.addEventListener("resize", syncHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", syncHeight);
      root.classList.remove("has-new-address-banner");
      root.style.removeProperty("--new-address-banner-height");
    };
  }, []);

  return (
    <div
      ref={bannerRef}
      className="new-address-banner"
      role="region"
      aria-label="Dashboard address change notice"
    >
      <p className="new-address-banner__message">
        Uzeli Dashboard now has a new address:{" "}
        <a
          className="new-address-banner__address"
          href={NEW_DASHBOARD_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          app.uzeli.com
        </a>
        . This page will start redirecting to the new address on{" "}
        {REDIRECT_DATE_LABEL}. Same dashboard features you have loved, just
        better.
      </p>
      <a
        className="new-address-banner__cta"
        href={NEW_DASHBOARD_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Use New Version
      </a>
    </div>
  );
};

export default NewAddressBanner;
